import {
  useNavigate,
  useRouter,
  useRouterState,
  type NavigateOptions,
  type RegisteredRouter,
} from '@tanstack/react-router';

import { useSyncExternalStore } from 'react';

import { pageEntranceKey } from './usePageEntrance';

/**
 * Where each history entry was opened from, so Back, Cancel and Save go to the page the user
 * actually came from (a details page, a filtered table page) instead of a fixed list route.
 *
 * Returning is a forward navigation to that href, never `history.back()`, so the browser keeps
 * no forward entry. The entry created by a return inherits the origin of the page it returns
 * to, so its own Back keeps walking up the trail instead of bouncing to the editor.
 */

interface ReturnTarget {
  href: string;
  key: string;
  /** The parsed location, so Back can render a router Link to it. */
  pathname?: string | undefined;
  search?: Record<string, unknown> | undefined;
}

interface TrackedLocation {
  href: string;
  pathname: string;
  search?: object | undefined;
  state: { key?: string | undefined; __TSR_key?: string | undefined };
}

interface ReturnRecords {
  targets: Record<string, ReturnTarget>;
  order: string[];
  pendingReturnOf?: string | undefined;
}

const storageKey = 'shigo.return-navigation';
// Origins are recorded after a page renders (router `onResolved`); subscribers re-render then.
const listeners = new Set<() => void>();
let version = 0;

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getVersion() {
  return version;
}
const maxRecords = 200;

function readRecords(): ReturnRecords {
  try {
    const stored = window.sessionStorage.getItem(storageKey);
    if (stored) return JSON.parse(stored) as ReturnRecords;
  } catch {
    // Storage can be unavailable (private mode); fall back to the default destinations.
  }
  return { targets: {}, order: [] };
}

function writeRecords(records: ReturnRecords) {
  if (records.order.length > maxRecords) {
    const dropped = new Set(records.order.splice(0, records.order.length - maxRecords));
    records.targets = Object.fromEntries(
      Object.entries(records.targets).filter(([key]) => !dropped.has(key)),
    );
  }
  try {
    window.sessionStorage.setItem(storageKey, JSON.stringify(records));
  } catch {
    // Best effort only.
  }
}

/**
 * The recorded origin of `key`. `recordsVersion` is the store version the caller rendered with:
 * it makes the read a dependency of that version, so memoization (React Compiler) re-reads it.
 */
function readTarget(key: string | null, recordsVersion: number) {
  void recordsVersion;
  return key ? readRecords().targets[key] : undefined;
}

function locationKey(location: TrackedLocation) {
  return location.state.__TSR_key ?? location.state.key ?? null;
}

/** Records the origin of the entry just resolved. Exported for tests. */
export function recordResolvedLocation(
  from: TrackedLocation | undefined,
  to: TrackedLocation,
): void {
  const toKey = locationKey(to);
  if (!toKey) return;
  const records = readRecords();
  const pendingReturnOf = records.pendingReturnOf;
  records.pendingReturnOf = undefined;
  // Revisiting an entry with Back/Forward keeps the origin it was opened with.
  if (records.targets[toKey]) {
    writeRecords(records);
    return;
  }
  const fromKey = from ? locationKey(from) : null;
  let target: ReturnTarget | undefined;
  if (pendingReturnOf) target = records.targets[pendingReturnOf];
  // Search changes and tabs of one details page are the same page: keep that page's origin.
  else if (from && fromKey && pageEntranceKey(from.pathname) === pageEntranceKey(to.pathname))
    target = records.targets[fromKey];
  else if (from && fromKey)
    target = {
      href: from.href,
      key: fromKey,
      pathname: from.pathname,
      search: { ...(from.search ?? {}) },
    };
  if (target) {
    records.targets[toKey] = target;
    records.order.push(toKey);
  }
  writeRecords(records);
  version += 1;
  listeners.forEach((listener) => {
    listener();
  });
}

export function startReturnNavigationTracking(router: {
  subscribe: (
    event: 'onResolved',
    listener: (event: { fromLocation?: TrackedLocation; toLocation: TrackedLocation }) => void,
  ) => () => void;
}) {
  return router.subscribe('onResolved', ({ fromLocation, toLocation }) => {
    recordResolvedLocation(fromLocation, toLocation);
  });
}

type AnyNavigateOptions = Parameters<ReturnType<typeof useNavigate>>[0];

declare const returnFallbackBrand: unique symbol;

/** A fallback destination checked against the route tree; see `returnFallback`. */
export type ReturnFallback = { readonly [returnFallbackBrand]: true };

/** A fallback: a literal checked against the route tree, or a value from `returnFallback`. */
export type ReturnFallbackInput<TTo extends string | undefined> =
  ReturnFallback | NavigateOptions<RegisteredRouter, string, TTo>;

/**
 * Checks one destination (route, params, search) and marks it as a fallback, so a fallback
 * that depends on a condition can be typed branch by branch:
 * `scope ? returnFallback({ to: '/a/$agencyId', params }) : returnFallback({ to: '/campaigns' })`.
 */
export function returnFallback<TTo extends string | undefined>(
  options: NavigateOptions<RegisteredRouter, string, TTo>,
): ReturnFallback {
  return options as unknown as ReturnFallback;
}

/**
 * A fallback that is already a resolved path (e.g. campaign hierarchy links), which cannot be
 * checked against route templates.
 */
export function returnFallbackFromPath(link: {
  to: string;
  search?: Record<string, string> | undefined;
}): ReturnFallback {
  return { to: link.to, search: link.search ?? {} } as unknown as ReturnFallback;
}

/**
 * The page the current entry was opened from, or `fallback` when there is none
 * (a direct link, a new tab). Use `replace` after a successful save so Back skips the form, and
 * `ignoreBlocker` where the save already settled the form the unsaved-changes guard watches.
 */
export function useReturnNavigation<TTo extends string | undefined>(
  fallbackInput: ReturnFallbackInput<TTo>,
) {
  const fallback = fallbackInput as unknown as AnyNavigateOptions;
  const navigate = useNavigate();
  const router = useRouter();
  const current = useRouterState({ select: (state) => state.location });
  const key = locationKey(current);
  const recordsVersion = useSyncExternalStore(subscribe, getVersion, getVersion);
  const target = readTarget(key, recordsVersion);
  const href = target?.href ?? router.buildLocation(fallback).href;
  // Router Link options for the same destination as `href`.
  const link: AnyNavigateOptions = target?.pathname
    ? ({ to: target.pathname, search: target.search ?? {} } as unknown as AnyNavigateOptions)
    : fallback;

  /** Marks the next resolved entry as a return, so it inherits the origin of the page it reopens. */
  function markReturn() {
    const latest = key ? readRecords().targets[key] : undefined;
    if (!latest) return null;
    const records = readRecords();
    records.pendingReturnOf = latest.key;
    writeRecords(records);
    return latest;
  }

  async function returnTo(options: { replace?: boolean; ignoreBlocker?: boolean } = {}) {
    const navigation = {
      replace: options.replace ?? false,
      ...(options.ignoreBlocker ? { ignoreBlocker: true } : {}),
    };
    const latest = markReturn();
    await navigate(latest ? { href: latest.href, ...navigation } : { ...fallback, ...navigation });
  }

  return { href, link, markReturn, returnTo };
}
